import {
  ActForm,
  Organization,
  UUID,
  WorkLabels,
  AssigneesProjectResponseType,
} from '@builddocs/data-management'
import {
  AOSRType,
  Application,
  ExecutiveDrawing,
  ExecutiveScheme,
  Laboratory,
  LoadableApplicationKeys,
  Supporting,
} from '@builddocs/filestore-api'
import {
  Card,
  Box,
  Typography,
  CardHeader,
  CardContent,
  CardActions,
  Button,
  Stack,
  Checkbox,
  FormGroup,
} from '@mui/material'
import FormControlLabel from '@mui/material/FormControlLabel'
import format from 'date-fns/format'
import { ru } from 'date-fns/locale'
import parseISO from 'date-fns/parseISO'
import _ from 'lodash'

import AssigneesDialog from '../AssigneesDialog'
import { AssigneesDialogAosr } from '../AssigneesDialogAosr'
import EditAssigneesDialog from '../EditAssigneesDialog'
import EditTextField from '../EditTextField'
import MaterialDialog from '../MaterialDialog'
import OrganizationDialog from '../OrganizationDialog'
import { RowPDFView } from '../RowPDFView'
import SelectingDocuments from '../SelectingDocuments'
import { AOSRJsonType, SecondaryActJsonType } from '../Types/dataJSON'
import { ActSectionBase, ISelectActSection } from '../viewer/template.type'
import WorksDialog from '../WorksDialog'

export const fieldForAddress: Record<LoadableApplicationKeys, string> = {
  [Application.LABORATORY]: 'laboratories',
  [Application.CERTIFICATE]: 'certificates',
  [Application.EXECUTIVE_SCHEME]: 'executiveSchemes',
  [Application.PERMIT]: 'permits',
  [Application.SUPPORTING]: 'supportingDocs',
  [Application.NORMATIVE]: 'normatives',
  [Application.NORMATIVE_DOC]: 'normatives',
  [Application.WORKING_DRAFT]: 'workingDrafts',
  [Application.EXECUTIVE_DRAWING]: 'executiveDrawings',
  [Application.ADDITIONAL_DOCUMENT]: 'additionalDocs',
}

export const getEditField = ({
  state,
  dataJSON,
  act,
  onChange,
  onSelectDoc,
  onShowCertAOSR,
  showCertAOSR,
  onGenerateRegistry,
  shouldGenerateRegistry,
  onSelectShowInfoAboutOrg,
  forceIncludeOrganization,
  onEditRepresentativesInRegistry,
  assigneesForActRegistry,
  setShouldIncludeSupportingsInProjectDocuments,
  shouldIncludeSupportingsInProjectDocuments,
  onSelectAssigneesForAOSR,
}: {
  state: { key: string; title: string }
  dataJSON: AOSRJsonType
  act: AOSRType
  onChange: (e: string, address: string) => void
  onSelectDoc: (
    docs: Record<string, any>[],
    repository: string,
    address: string,
  ) => void
  onRejectDocs: (address: string) => void
  onShowCertAOSR: () => void
  showCertAOSR: boolean
  onGenerateRegistry: () => void
  shouldGenerateRegistry: boolean
  onSelectAssigneesForAOSR: (signers: unknown[] | unknown, key: string) => void
  onSelectOrgToFieldForAOSR: (
    org: {
      inn: string
      ogrn: string
      name: string
      physicalAddress: { unrestrictedValue: string }
    } | null,
    key: string,
  ) => void
  onSelectShowInfoAboutOrg: (key: string) => void
  forceIncludeOrganization: string[] | undefined
  onEditRepresentativesInRegistry: (reps: Record<string, UUID[]>) => void
  assigneesForActRegistry: Record<string, UUID[]> | null
  setShouldIncludeSupportingsInProjectDocuments: (value: boolean) => void
  shouldIncludeSupportingsInProjectDocuments: boolean
}) => {
  const completedWorks = dataJSON.acceptance_object?.completedWorks ?? undefined

  switch (state.key) {
    case 'acceptance_object': {
      return (
        <Card variant="outlined">
          <CardHeader title={state.title} />
          <CardContent>
            <Stack spacing={3}>
              {completedWorks.works.map((work, workIndex) => {
                return (
                  <Box key={workIndex} height="min-content">
                    <Typography variant="caption">
                      {WorkLabels[work.workType]}
                    </Typography>
                    <Typography gutterBottom mb={2} variant="h6">
                      {work.title}
                    </Typography>
                    <Stack spacing={2}>
                      <EditTextField
                        key="place"
                        field="place"
                        id={'place' + workIndex}
                        value={work.place}
                        onSave={newValue =>
                          onChange(
                            newValue,
                            `${state.key}.completedWorks.works[${workIndex}].place`,
                          )
                        }
                      />
                      <Stack direction="row" spacing={2}>
                        <EditTextField
                          key="workDate"
                          disabled={!!work.ozhrItemIds?.length}
                          field="workDate"
                          id={'workDate' + workIndex}
                          value={work.workDate}
                          onSave={newValue =>
                            onChange(
                              newValue,
                              `${state.key}.completedWorks.works[${workIndex}].workDate`,
                            )
                          }
                        />
                        <EditTextField
                          key="workDateEnd"
                          disabled={!!work.ozhrItemIds?.length}
                          field="workDateEnd"
                          id={'workDateEnd' + workIndex}
                          value={work.workDateEnd ?? work.workDate}
                          onSave={newValue =>
                            onChange(
                              newValue,
                              `${state.key}.completedWorks.works[${workIndex}].workDateEnd`,
                            )
                          }
                        />
                      </Stack>
                    </Stack>
                  </Box>
                )
              })}
            </Stack>
          </CardContent>
        </Card>
      )
    }

    case 'project_documents': {
      return (
        <Card variant="outlined">
          <CardHeader title={state.title} />
          <CardContent>
            <Stack spacing={2}>
              {completedWorks.works.map((work, workIndex) => (
                <Stack key={workIndex}>
                  <Typography variant="h5">{work.title}</Typography>
                  <SelectingDocuments
                    key={state.key}
                    actStatus={act.approvalStatus}
                    documentType={Application.WORKING_DRAFT}
                    fieldsForUpdate={[
                      'name',
                      'cipher',
                      'sheet',
                      'organization',
                    ]}
                    initialDocs={
                      work.projectDocIds.map(
                        id => completedWorks.projectDocs[id],
                      ) ?? []
                    }
                    onChange={(value, field, docId) =>
                      onChange(
                        value,
                        `acceptance_object.completedWorks.projectDocs.${docId}.${field}`,
                      )
                    }
                    onSelect={docs =>
                      onSelectDoc(
                        docs,
                        'projectDocs',
                        `acceptance_object.completedWorks.works[${workIndex}].projectDocIds`,
                      )
                    }
                  />
                </Stack>
              ))}
            </Stack>
          </CardContent>
        </Card>
      )
    }

    case 'material': {
      const fieldsForUpdateCert = [
        'name',
        'number',
        'issueDate',
        'expirationDate',
      ]
      const fieldsForUpdateLab = ['name', 'number', 'issueDate']

      return (
        <Card variant="outlined">
          <CardHeader title={state.title} />
          <CardContent>
            <FormControlLabel
              control={
                <Checkbox
                  checked={shouldGenerateRegistry}
                  id={`${state.title}-shouldGenerateRegistry`}
                  onChange={onGenerateRegistry}
                />
              }
              label="Сформировать реестр приложений"
            />
            <EditAssigneesDialog
              initialRepresentatives={assigneesForActRegistry}
              onEditList={onEditRepresentativesInRegistry}
            />
            {completedWorks.works.map((work, workIndex) => {
              return (
                <Stack key={workIndex} mt={2} width="max-content">
                  <Typography fontWeight="bolder" variant="h5">
                    {work.title}
                  </Typography>
                  {work.materialIds.map(id => (
                    <Stack key={id} mt={2}>
                      <Typography variant="h6">
                        {completedWorks.materials[id].materialName}
                      </Typography>
                      <SelectingDocuments
                        key={'certificates' + id}
                        actStatus={act.approvalStatus}
                        documentType={Application.CERTIFICATE}
                        fieldsForUpdate={fieldsForUpdateCert}
                        initialDocs={
                          completedWorks.materials[id].certificateIds.map(
                            _id => completedWorks.certificates[_id],
                          ) ?? []
                        }
                        workDate={work.workDate}
                        onChange={(value, field, docId) =>
                          onChange(
                            value,
                            `acceptance_object.completedWorks.certificates.${docId}.${field}`,
                          )
                        }
                        onSelect={docs =>
                          onSelectDoc(
                            docs,
                            'certificates',
                            `acceptance_object.completedWorks.materials[${id}].certificateIds`,
                          )
                        }
                      />
                      <SelectingDocuments
                        key={'laboratories' + id}
                        actStatus={act.approvalStatus}
                        documentType={Application.LABORATORY}
                        fieldsForUpdate={fieldsForUpdateLab}
                        initialDocs={
                          completedWorks.materials[id].laboratoryIds.map(
                            _id => completedWorks.laboratories[_id],
                          ) ?? []
                        }
                        onChange={(value, field, docId) =>
                          onChange(
                            value,
                            `acceptance_object.completedWorks.laboratories.${docId}.${field}`,
                          )
                        }
                        onSelect={docs =>
                          onSelectDoc(
                            docs,
                            'laboratories',
                            `acceptance_object.completedWorks.materials[${id}].laboratoryIds`,
                          )
                        }
                      />
                    </Stack>
                  ))}
                </Stack>
              )
            })}
          </CardContent>
        </Card>
      )
    }

    case 'documents': {
      return (
        <Card variant="outlined">
          <CardHeader title={state.title} />
          <CardContent>
            <FormControlLabel
              control={
                <Checkbox
                  checked={shouldGenerateRegistry}
                  id={`${state.title}-shouldGenerateRegistry`}
                  onChange={onGenerateRegistry}
                />
              }
              label="Сформировать реестр приложений"
            />
            <EditAssigneesDialog
              initialRepresentatives={assigneesForActRegistry}
              onEditList={onEditRepresentativesInRegistry}
            />
            <SelectingDocuments
              key={'laboratoryIds' + state.key}
              actStatus={act.approvalStatus}
              attachedDocuments={act.attachedDocuments}
              documentType={Application.LABORATORY}
              fieldsForUpdate={['name', 'number', 'issueDate']}
              initialDocs={
                completedWorks.aosrDocs.laboratoryIds.map(
                  id =>
                    completedWorks.laboratories[id] ?? {
                      id,
                      name: '',
                      number: '',
                      issueDate: '',
                      documentUrl: '',
                    },
                ) as never as Laboratory[]
              }
              onChange={(value, field, docId) =>
                onChange(
                  value,
                  `acceptance_object.completedWorks.laboratories.${docId}.${field}`,
                )
              }
              onSelect={docs =>
                onSelectDoc(
                  docs,
                  'laboratories',
                  `acceptance_object.completedWorks.aosrDocs.laboratoryIds`,
                )
              }
            />
            <SelectingDocuments
              key={'executiveSchemeIds' + state.key}
              actStatus={act.approvalStatus}
              attachedDocuments={act.attachedDocuments}
              documentType={Application.EXECUTIVE_SCHEME}
              fieldsForUpdate={['name', 'cipher', 'sheet', 'issueDate']}
              initialDocs={
                (completedWorks.aosrDocs.executiveSchemeIds.map(
                  id =>
                    completedWorks.executiveSchemes[id] ?? {
                      id,
                      name: '',
                      number: '',
                      issueDate: '',
                      documentUrl: '',
                    },
                ) ?? []) as never as ExecutiveScheme[]
              }
              onChange={(value, field, docId) =>
                onChange(
                  value,
                  `acceptance_object.completedWorks.executiveSchemes.${docId}.${field}`,
                )
              }
              onSelect={docs =>
                onSelectDoc(
                  docs,
                  'executiveSchemes',
                  `acceptance_object.completedWorks.aosrDocs.executiveSchemeIds`,
                )
              }
            />
            <SelectingDocuments
              key={'executiveDrawingIds' + state.key}
              actStatus={act.approvalStatus}
              attachedDocuments={act.attachedDocuments}
              documentType={Application.EXECUTIVE_DRAWING}
              fieldsForUpdate={['name', 'cipher', 'sheet', 'issueDate']}
              initialDocs={
                (completedWorks.aosrDocs?.executiveDrawingIds?.map(
                  id =>
                    completedWorks.executiveDrawings[id] ?? {
                      id,
                      name: '',
                      number: '',
                      issueDate: '',
                      documentUrl: '',
                    },
                ) ?? []) as never as ExecutiveDrawing[]
              }
              onChange={(value, field, docId) =>
                onChange(
                  value,
                  `acceptance_object.completedWorks.executiveDrawings.${docId}.${field}`,
                )
              }
              onSelect={docs =>
                onSelectDoc(
                  docs,
                  'executiveDrawings',
                  `acceptance_object.completedWorks.aosrDocs.executiveDrawingIds`,
                )
              }
            />
          </CardContent>
        </Card>
      )
    }

    case 'documents_for_work': {
      return (
        <Stack py={2} spacing={1}>
          <Card variant="outlined">
            <CardHeader title={state.title} />
            <CardContent>
              {completedWorks.works.map((work, workIndex) => (
                <Stack key={workIndex} mt={2}>
                  <Typography variant="h5">{work.title}</Typography>
                  <SelectingDocuments
                    key={'normativeDocIds' + state.key}
                    actStatus={act.approvalStatus}
                    documentType={Application.NORMATIVE}
                    fieldsForUpdate={[]}
                    initialDocs={work.normativeDocIds.map(
                      id => completedWorks.normativeDocs[id],
                    )}
                    onSelect={docs =>
                      onSelectDoc(
                        docs,
                        'normativeDocs',
                        `acceptance_object.completedWorks.works[${workIndex}].normativeDocIds`,
                      )
                    }
                  />
                </Stack>
              ))}
            </CardContent>
          </Card>
          <Card variant="outlined">
            <CardContent>
              <SelectingDocuments
                key={'supportingDocsForWorks' + state.key}
                actStatus={act.approvalStatus}
                documentType={Application.SUPPORTING}
                fieldsForUpdate={[]}
                initialDocs={
                  ((dataJSON?.documents_for_work?.supportingDocsForWorks &&
                    dataJSON?.documents_for_work?.supportingDocsForWorksIds?.map(
                      id =>
                        dataJSON?.documents_for_work?.supportingDocsForWorks[
                          id
                        ] ?? {
                          id,
                          name: '',
                          number: '',
                          issueDate: '',
                          documentUrl: '',
                        },
                    )) as never as Supporting[]) || []
                }
                onSelect={docs =>
                  onSelectDoc(
                    docs,
                    'supportingDocsForWorks',
                    `documents_for_work.supportingDocsForWorksIds`,
                  )
                }
              />
              <CardActions>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={shouldIncludeSupportingsInProjectDocuments}
                      onChange={(_e, checked) =>
                        setShouldIncludeSupportingsInProjectDocuments(checked)
                      }
                    />
                  }
                  label="Отображать в п.2"
                />
              </CardActions>
            </CardContent>
          </Card>
        </Stack>
      )
    }

    case 'representative_developer_inspector':
    case 'representative_executor_building':
    case 'representative_executor_building_inspector':
    case 'representative_executor_works':
    case 'representative_other':
    case 'representative_designer': {
      return (
        <Card variant="outlined">
          <CardHeader title={state.title} />
          <CardContent>
            <Stack direction="column">
              <AssigneesDialogAosr
                // Стоит специально, чтобы при выборе другого поля таблица сбрасывалась.
                key={state.key + '_assignees'}
                act={act}
                assigneeKey={state.key}
                dataJSON={dataJSON}
                workType={act.workType}
                onSelect={assignees =>
                  onSelectAssigneesForAOSR(assignees, state.key)
                }
              />
              <Stack alignItems="center" direction="row">
                <Checkbox
                  checked={
                    forceIncludeOrganization &&
                    forceIncludeOrganization.includes(state.key)
                  }
                  onChange={() => onSelectShowInfoAboutOrg(state.key)}
                />
                <Typography variant="subtitle2">
                  Добавить информацию об организации
                </Typography>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      )
    }

    case 'applications': {
      return (
        <Stack mt={2} spacing={2} width="450px">
          <Typography fontWeight="bolder">Приложения</Typography>
          <Stack alignItems="center" direction="row">
            <Checkbox checked={showCertAOSR} onChange={onShowCertAOSR} />
            <Typography variant="subtitle2">
              Добавить сертификаты в пункт Приложения
            </Typography>
          </Stack>
          <Stack spacing={2}>
            {!_.isEmpty(completedWorks.certificates) && (
              <Box>
                <Typography color="text.secondary" variant="subtitle2">
                  Сертификаты:
                </Typography>
                {Object.values(completedWorks.certificates).map(doc => (
                  <RowPDFView
                    key={doc.id}
                    id={doc.id}
                    link={doc.documentUrl ?? ''}
                    title={`${doc.name}  ${doc.number ?? 'б/н'} от ${
                      doc.issueDate
                        ? format(parseISO(doc.issueDate), 'd MMM yyyy г.', {
                            locale: ru,
                          })
                        : 'б/д'
                    }`}
                  />
                ))}
              </Box>
            )}
            {!_.isEmpty(completedWorks.executiveSchemes) && (
              <Box>
                <Typography color="text.secondary" variant="subtitle2">
                  Исполнительные схемы:
                </Typography>
                {Object.values(completedWorks.executiveSchemes).map(doc => (
                  <RowPDFView
                    key={doc.id}
                    id={doc.id}
                    link={doc.documentUrl ?? ''}
                    title={`${doc.name}  ${doc.number || 'б/н'} от ${
                      doc.issueDate
                        ? format(parseISO(doc.issueDate), 'd MMM yyyy г.', {
                            locale: ru,
                          })
                        : 'б/д'
                    }`}
                  />
                ))}
              </Box>
            )}

            {!_.isEmpty(completedWorks.laboratories) && (
              <Box>
                <Typography color="text.secondary" variant="subtitle2">
                  Лаборатории:
                </Typography>
                {Object.values(completedWorks.laboratories).map(doc => (
                  <RowPDFView
                    key={doc.id}
                    id={doc.id}
                    link={doc.documentUrl ?? ''}
                    title={`${doc.name}  ${doc.number ?? 'б/н'} от ${
                      doc.issueDate
                        ? format(parseISO(doc.issueDate), 'd MMM yyyy г.', {
                            locale: ru,
                          })
                        : 'б/д'
                    }`}
                  />
                ))}
              </Box>
            )}
            <SelectingDocuments
              key={'supportingIds' + state.key}
              actStatus={act.approvalStatus}
              documentType={Application.SUPPORTING}
              fieldsForUpdate={[]}
              initialDocs={
                (dataJSON.applications?.supportingDocs &&
                  dataJSON?.applications?.supportingIds?.map(
                    id => dataJSON.applications.supportingDocs[id],
                  )) ||
                []
              }
              onSelect={docs =>
                onSelectDoc(
                  docs,
                  'supportingDocs',
                  `applications.supportingIds`,
                )
              }
            />
          </Stack>
        </Stack>
      )
    }

    default:
      return (
        <Card variant="outlined">
          <CardHeader title={state.title} />
          <CardContent>
            <EditTextField
              key={state.key}
              id={state.key}
              value={dataJSON[state.key]?.value}
              onSave={newValue => onChange(newValue, `${state.key}.value`)}
            />
          </CardContent>
        </Card>
      )
  }
}

export const getEditFieldSecondaryActs = ({
  state,
  dataJSON,
  initValue,
  onChange,
  onSelectDoc,
  onSelectAssignees,
  onSelectOrgToField,
  onSelectOrg,
  onSelectMaterial,
  onSelectWork,
  onGenerateRegistry,
  shouldGenerateRegistry,
  onEditRepresentativesInRegistry,
  assigneesForActRegistry,
  shouldShowMaterials,
  shouldShowLaboratories,
  shouldShowExecutiveSchemes,
  onAddMaterials,
  onAddLaboratories,
  onAddExecutiveSchemes,
  onSelectShowInfoAboutOrg,
  forceIncludeOrganization,
}: {
  state: {
    key: string
    title: string
    editable: ActSectionBase['editable']
    dataType: ActSectionBase['dataType']
    documentTypes?: LoadableApplicationKeys[]
    showPosition?: boolean
  }
  dataJSON: SecondaryActJsonType
  initValue: SecondaryActJsonType
  onChange: (e: string, address: string) => void
  onSelectDoc: (
    docs: Record<string, any>[],
    repository: string,
    address?: string,
  ) => void
  onRejectDocs: (address) => void
  onSelectAssignees: (
    signers: AssigneesProjectResponseType[],
    key: string,
    showPosition?: boolean,
  ) => void
  onSelectOrg: (org: Organization | undefined, key: string) => void
  onSelectOrgToField: (
    org: {
      inn: string
      ogrn: string
      name: string
      physicalAddress: { unrestrictedValue: string }
    } | null,
    key: string,
  ) => void
  onSelectMaterial: (materialIds: UUID[], key: string) => void
  onSelectWork: (sectionIds: UUID[], key: string) => void
  onSelectShowInfoAboutOrg: (key: string) => void
  onGenerateRegistry: () => void
  shouldGenerateRegistry: boolean
  onEditRepresentativesInRegistry: (reps: Record<string, UUID[]>) => void
  assigneesForActRegistry: Record<string, UUID[]> | null
  shouldShowMaterials: boolean
  shouldShowLaboratories: boolean
  shouldShowExecutiveSchemes: boolean
  onAddMaterials: (value: boolean) => void
  onAddLaboratories: (value: boolean) => void
  onAddExecutiveSchemes: (value: boolean) => void
  forceIncludeOrganization: string[] | undefined
}) => {
  if (state.key.includes('table')) {
    return null
  }
  switch (state.dataType) {
    case 'DOCUMENTS':
      return (
        <Card variant="outlined">
          <CardHeader title={state.title} />
          <CardContent>
            {(state.key === 'applications' ||
              ((state.key === 'works_info' ||
                state.key === 'documents_executive_schemes' ||
                state.key === 'documents_laboratories' ||
                state.key === 'test_documents') &&
                dataJSON.act_type === ActForm.AOOK)) && (
              <Stack direction="column" spacing={2} width="fit-content">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={shouldGenerateRegistry}
                      id={`${state.title}-shouldGenerateRegistry`}
                      onChange={onGenerateRegistry}
                    />
                  }
                  label="Сформировать реестр приложений"
                />
                <EditAssigneesDialog
                  initialRepresentatives={assigneesForActRegistry}
                  onEditList={onEditRepresentativesInRegistry}
                />
              </Stack>
            )}
            {state?.documentTypes?.map(type => (
              <>
                {state.key === 'documents_executive_schemes' &&
                  type === Application.EXECUTIVE_SCHEME && (
                    <FormGroup>
                      <FormControlLabel
                        checked={shouldShowExecutiveSchemes}
                        control={
                          <Checkbox
                            onChange={
                              onAddExecutiveSchemes
                                ? e => onAddExecutiveSchemes(e.target.checked)
                                : undefined
                            }
                          />
                        }
                        label="Отображать исполнительную документацию"
                      />
                    </FormGroup>
                  )}
                <SelectingDocuments
                  key={type}
                  documentType={type}
                  fieldsForUpdate={[]}
                  initialDocs={
                    state.key in dataJSON &&
                    'manualField' in dataJSON[state.key] &&
                    fieldForAddress[type] in dataJSON[state.key].manualField
                      ? dataJSON[state.key].manualField[fieldForAddress[type]]
                      : []
                  }
                  onSelect={docs =>
                    onSelectDoc(
                      docs,
                      `${state.key}.manualField.${fieldForAddress[type]}`,
                    )
                  }
                />
                {state.key === 'documents_laboratories' &&
                  state.documentTypes?.includes(Application.LABORATORY) && (
                    <FormGroup>
                      <FormControlLabel
                        checked={shouldShowLaboratories}
                        control={
                          <Checkbox
                            onChange={
                              onAddLaboratories
                                ? e => onAddLaboratories(e.target.checked)
                                : undefined
                            }
                          />
                        }
                        label="Отображать лаборатории"
                      />
                    </FormGroup>
                  )}
              </>
            ))}
          </CardContent>
        </Card>
      )

    case 'OTHER':
      return (
        <Stack width="100%">
          <Card variant="outlined">
            <CardHeader title={state.title} />
            <CardContent>
              <Stack spacing={2} width="100%">
                <EditTextField
                  field="other"
                  id={state.key + '_1'}
                  label="б) разрешается использование конструкций с нагружением в %:"
                  options={(state as unknown as ISelectActSection).options}
                  value={dataJSON.conclusion_1?.value || ''}
                  onSave={newValue => onChange(newValue, `conclusion_1.value`)}
                />
                <EditTextField
                  field="other"
                  id={state.key + '_2'}
                  label="в) разрешается полное нагружение при выполнении условий:"
                  options={(state as unknown as ISelectActSection).options}
                  value={dataJSON.conclusion_2?.value || ''}
                  onSave={newValue => onChange(newValue, `conclusion_2.value`)}
                />
                <EditTextField
                  field="other"
                  id={state.key + '_3'}
                  label="г) разрешается производство последующих работ:"
                  options={(state as unknown as ISelectActSection).options}
                  value={dataJSON.conclusion_3?.value || ''}
                  onSave={newValue => onChange(newValue, `conclusion_3.value`)}
                />
              </Stack>
            </CardContent>
            <CardActions>
              {initValue[state.key]?.value !== dataJSON[state.key]?.value && (
                <Button
                  onClick={() =>
                    onChange(initValue[state.key]?.value, `conclusion_3.value`)
                  }
                >
                  Отменить
                </Button>
              )}
            </CardActions>
          </Card>
        </Stack>
      )

    case 'ASSIGNEE':
    case 'SIGN':
      return (
        <Card variant="outlined">
          <CardHeader title={state.title} />
          <CardContent>
            <AssigneesDialog
              key={state.key}
              initialAssignees={
                dataJSON[state.key.replace('_sign', '')] &&
                'manualAssignees' in dataJSON[state.key.replace('_sign', '')]
                  ? Object.keys(
                      dataJSON[state.key.replace('_sign', '')].manualAssignees,
                    )
                  : []
              }
              isSingleSelect={!state.key.includes('other')}
              onSelect={(signers, org) => {
                onSelectAssignees(signers, state.key, state.showPosition)
                onSelectOrgToField(
                  org,
                  state.key.replace('_sign', '').replace('representative_', ''),
                )
              }}
            />
          </CardContent>
        </Card>
      )

    case 'ORGANIZATION':
      return (
        <Card variant="outlined">
          <CardHeader title={state.title} />
          <CardContent>
            <OrganizationDialog
              initialOrganization={dataJSON[state.key]?.manualOrganization}
              isSingleSelect={true}
              typeOrganization={state.title}
              onSelect={org => onSelectOrg(org, state.key)}
            />
          </CardContent>
        </Card>
      )

    case 'MATERIAL':
      return (
        <Card variant="outlined">
          <CardHeader title={state.title} />
          <CardContent>
            {(dataJSON.act_type === ActForm.AOOK ||
              dataJSON.act_type === ActForm.APOOK ||
              dataJSON.act_type === ActForm.ANMO) &&
              shouldShowMaterials && (
                <MaterialDialog
                  allMaterials={dataJSON[state.key].materials}
                  initialMaterials={
                    dataJSON[state.key]?.selectedMaterials ?? []
                  }
                  title="Добавить материал"
                  onSelect={materialIds =>
                    onSelectMaterial(materialIds, state.key)
                  }
                />
              )}
            {(dataJSON.act_type === ActForm.AOOK ||
              dataJSON.act_type === ActForm.APOOK ||
              dataJSON.act_type === ActForm.ANMO) && (
              <FormGroup>
                <FormControlLabel
                  checked={shouldShowMaterials}
                  control={
                    <Checkbox
                      onChange={
                        onAddMaterials
                          ? e => onAddMaterials(e.target.checked)
                          : undefined
                      }
                    />
                  }
                  label="Отображать материалы"
                />
              </FormGroup>
            )}
            {dataJSON.act_type === ActForm.AOOK && (
              <>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={shouldGenerateRegistry}
                      id={`${state.title}-shouldGenerateRegistry`}
                      onChange={onGenerateRegistry}
                    />
                  }
                  label="Сформировать реестр приложений"
                />
                <EditAssigneesDialog
                  initialRepresentatives={assigneesForActRegistry}
                  onEditList={onEditRepresentativesInRegistry}
                />
              </>
            )}
          </CardContent>
        </Card>
      )

    case 'WORK':
      return (
        <Card variant="outlined">
          <CardHeader title={state.title} />
          <CardContent>
            <WorksDialog
              allWorks={dataJSON[state.key].works}
              initialWorks={dataJSON[state.key]?.selectedWorks ?? []}
              title="Добавить работу"
              onSelect={sectionIds => onSelectWork(sectionIds, state.key)}
            />
          </CardContent>
        </Card>
      )

    case 'SELECT':
      return (
        <Card variant="outlined">
          <CardHeader title={state.title} />
          <CardContent>
            <EditTextField
              key={state.key}
              field="select"
              id={state.key}
              options={(state as unknown as ISelectActSection).options}
              value={dataJSON[state.key]?.value}
              onSave={newValue => onChange(newValue, `${state.key}.value`)}
            />
          </CardContent>
        </Card>
      )

    case 'DATE':
      return (
        <Card variant="outlined">
          <CardHeader title={state.title} />
          <CardContent>
            <EditTextField
              key={state.key}
              field="date"
              id={state.key}
              value={dataJSON[state.key]?.value}
              onSave={newValue => onChange(newValue, `${state.key}.value`)}
            />
          </CardContent>
        </Card>
      )

    default:
      switch (state.key) {
        case 'representative_developer_inspector':
        case 'representative_executor_building':
        case 'representative_executor_building_inspector':
        case 'representative_executor_works':
        case 'representative_other':
        case 'representative_designer': {
          return (
            <Card variant="outlined">
              <CardHeader title={state.title} />
              <CardContent>
                <Stack alignItems="center" direction="row">
                  <Checkbox
                    checked={
                      forceIncludeOrganization &&
                      forceIncludeOrganization.includes(state.key)
                    }
                    onChange={() => onSelectShowInfoAboutOrg(state.key)}
                  />
                  <Typography variant="subtitle2">
                    Добавить информацию об организации
                  </Typography>
                </Stack>
              </CardContent>
            </Card>
          )
        }

        default:
          return (
            <Card variant="outlined">
              <CardHeader title={state.title} />
              <CardContent>
                <EditTextField
                  key={state.key}
                  id={state.key}
                  value={dataJSON[state.key]?.value}
                  onSave={newValue => onChange(newValue, `${state.key}.value`)}
                />
              </CardContent>
            </Card>
          )
      }
  }
}
